import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import {
  listStudentsByClass,
  listStudentsByGroup,
  listStudentsByAdmissionDate,
  singleSearchStudent,
  getSectionWiseSummary,
  getGenderReligionSummary,
} from "../../api/studentReportApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const groupApi = createLookupApi("groups");
const academicYearApi = createLookupApi("academic-years");

const STUDENT_COLUMNS = [
  { key: "customStudentId", label: "Student ID" },
  { key: "studentName", label: "Name" },
  { key: "studentRoll", label: "Roll" },
  { key: "classConfigName", label: "Class" },
  { key: "groupName", label: "Group" },
  { key: "guardianMobile", label: "Guardian Mobile" },
];

/**
 * Consolidates digicampusbd's `StudentReportController` — the real,
 * permission-granted slice of it (see backend `StudentReportService`
 * javadoc for the full scoping rationale and what's excluded). Each old url
 * was its own single-purpose screen (one filter combination, one table);
 * here they're tabs over the same shared table component, same
 * consolidation pattern already used for Sms/Payslip/FeeConfig.
 * `student-report-basic-info-list` (section-wise) and `student-info` are
 * NOT tabs here — both are already fully covered by the existing
 * `StudentsListPage` (search by academic year + class-configuration, list,
 * edit/disable/delete), so they're wired straight to that page in
 * pageRegistry.js instead of being duplicated. `student-multiple-report`
 * (the old app's own most complex variant — district/thana/shift/section/
 * category filters together) is only partially covered here (its class+year
 * filter combination, via the "Class Wise" tab) — see MIGRATION.md.
 */
// Real menu url -> the tab it should open on, so each of the old app's
// separate single-purpose screens still lands on its own matching view here.
const URL_TO_TAB = {
  "student-multiple-report": "class-wise",
  "student-report-basic-info-list-class-wise": "class-wise",
  "student-report-basic-info-list-group-wise": "group-wise",
  "student-report-date-wise-admission": "admission-date",
  "student-single-search": "single-search",
  "student-report-overview": "single-search",
  "student-report-section-wise-summary": "section-summary",
  "section-wise-religion": "gender-religion",
  sectionwisegender: "gender-religion",
};

export default function StudentReportsPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "class-wise");
  const [error, setError] = useState("");
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  useEffect(() => {
    classApi.list().then(setClasses);
    groupApi.list().then(setGroups);
    listClassConfigurations().then(setClassConfigurations);
    academicYearApi.list().then(setAcademicYears);
  }, []);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Student Reports</h1>
      </div>
      <Tabs
        tabs={[
          { key: "class-wise", label: "Class Wise" },
          { key: "group-wise", label: "Group Wise" },
          { key: "admission-date", label: "Admission Date Wise" },
          { key: "single-search", label: "Single Search" },
          { key: "section-summary", label: "Section Summary" },
          { key: "gender-religion", label: "Gender & Religion Summary" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      {tab === "class-wise" && <ClassWiseTab classes={classes} academicYears={academicYears} setError={setError} />}
      {tab === "group-wise" && (
        <GroupWiseTab classConfigurations={classConfigurations} groups={groups} academicYears={academicYears} setError={setError} />
      )}
      {tab === "admission-date" && (
        <AdmissionDateTab classConfigurations={classConfigurations} academicYears={academicYears} setError={setError} />
      )}
      {tab === "single-search" && <SingleSearchTab setError={setError} />}
      {tab === "section-summary" && <SectionSummaryTab academicYears={academicYears} setError={setError} />}
      {tab === "gender-religion" && <GenderReligionTab classes={classes} academicYears={academicYears} setError={setError} />}
    </MainLayout>
  );
}

function ClassWiseTab({ classes, academicYears, setError }) {
  const [classId, setClassId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!classId || !academicYear) {
      setError("Select a class and academic year.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setRows(await listStudentsByClass(classId, academicYear));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            <option value="">Select year</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      {rows && <DataTable columns={STUDENT_COLUMNS} rows={rows} />}
    </div>
  );
}

function GroupWiseTab({ classConfigurations, groups, academicYears, setError }) {
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!classConfigurationId || !groupId || !academicYear) {
      setError("Select a class configuration, a group, and academic year.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setRows(await listStudentsByGroup(classConfigurationId, groupId, academicYear));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="attendance-filters">
        <label>
          Class Configuration
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            <option value="">Select</option>
            {classConfigurations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Select group</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            <option value="">Select year</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      {rows && <DataTable columns={STUDENT_COLUMNS} rows={rows} />}
    </div>
  );
}

function AdmissionDateTab({ classConfigurations, academicYears, setError }) {
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!academicYear || !fromDate || !toDate) {
      setError("Select an academic year and a date range.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setRows(await listStudentsByAdmissionDate(classConfigurationId || undefined, academicYear, fromDate, toDate));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="attendance-filters">
        <label>
          Class Configuration (optional)
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            <option value="">All</option>
            {classConfigurations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            <option value="">Select year</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      {rows && <DataTable columns={[...STUDENT_COLUMNS, { key: "registrationDate", label: "Registered" }]} rows={rows} />}
    </div>
  );
}

function SingleSearchTab({ setError }) {
  const [customStudentId, setCustomStudentId] = useState("");
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!customStudentId) {
      setError("Enter a student ID.");
      return;
    }
    setError("");
    setLoading(true);
    setStudent(null);
    try {
      setStudent(await singleSearchStudent(customStudentId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Student not found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="attendance-filters">
        <label>
          Student ID
          <input value={customStudentId} onChange={(e) => setCustomStudentId(e.target.value)} placeholder="e.g. 20230246" />
        </label>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </div>
      {student && (
        <div className="table-scroll">
          <table className="attendance-table">
            <tbody>
              <tr>
                <th>Student ID</th>
                <td>{student.customStudentId}</td>
              </tr>
              <tr>
                <th>Name</th>
                <td>{student.studentName}</td>
              </tr>
              <tr>
                <th>Roll</th>
                <td>{student.studentRoll}</td>
              </tr>
              <tr>
                <th>Class</th>
                <td>{student.classConfigName}</td>
              </tr>
              <tr>
                <th>Group</th>
                <td>{student.groupName}</td>
              </tr>
              <tr>
                <th>Gender</th>
                <td>{student.studentGender}</td>
              </tr>
              <tr>
                <th>Date of Birth</th>
                <td>{student.studentDob}</td>
              </tr>
              <tr>
                <th>Religion</th>
                <td>{student.studentReligion}</td>
              </tr>
              <tr>
                <th>Father's Name</th>
                <td>{student.fatherName}</td>
              </tr>
              <tr>
                <th>Mother's Name</th>
                <td>{student.motherName}</td>
              </tr>
              <tr>
                <th>Guardian Mobile</th>
                <td>{student.guardianMobile}</td>
              </tr>
              <tr>
                <th>Academic Year</th>
                <td>{student.academicYear}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{student.studentStatus ? "Active" : "Inactive"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SectionSummaryTab({ academicYears, setError }) {
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!academicYear) {
      setError("Select an academic year.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setRows(await getSectionWiseSummary(academicYear));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load summary.");
    } finally {
      setLoading(false);
    }
  };

  const total = rows ? rows.reduce((sum, r) => sum + r.totalStudent, 0) : 0;

  return (
    <div>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            <option value="">Select year</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      {rows && (
        <>
          <DataTable
            columns={[
              { key: "className", label: "Class Name" },
              { key: "shiftName", label: "Shift Name" },
              { key: "sectionName", label: "Section Name" },
              { key: "totalStudent", label: "Total Student" },
            ]}
            rows={rows}
          />
          <p>
            <strong>Total Student: {total}</strong>
          </p>
        </>
      )}
    </div>
  );
}

function GenderReligionTab({ classes, academicYears, setError }) {
  const [classIds, setClassIds] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleClass = (id) => setClassIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  const search = async () => {
    if (!academicYear) {
      setError("Select an academic year.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setRows(await getGenderReligionSummary(classIds, academicYear));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to load summary.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <label>Academic Year</label>
      <br />
      <div className="attendance-filters" style={{ marginBottom: 8 }}>
        <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
          <option value="">Select year</option>
          {academicYears.map((y) => (
            <option key={y.id} value={y.name}>
              {y.name}
            </option>
          ))}
        </select>
      </div>
      <label>Classes (blank = all)</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "0.5rem 0" }}>
        {classes.map((c) => (
          <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: "normal" }}>
            <input type="checkbox" checked={classIds.includes(c.id)} onChange={() => toggleClass(c.id)} />
            {c.name}
          </label>
        ))}
      </div>
      <div className="form-actions" style={{ justifyContent: "flex-start" }}>
        <button type="button" className="btn-primary" onClick={search} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>
      {rows && (
        <DataTable
          columns={[
            { key: "className", label: "Class Name" },
            { key: "totalStudent", label: "Total Student" },
            { key: "male", label: "Male" },
            { key: "female", label: "Female" },
            { key: "islam", label: "Islam" },
            { key: "hindu", label: "Hindu" },
            { key: "buddhist", label: "Buddhist" },
            { key: "christian", label: "Christian" },
            { key: "others", label: "Others" },
          ]}
          rows={rows}
        />
      )}
    </div>
  );
}
